import { useState } from "react";
import { Link } from "wouter";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { PhysioLayout } from "@/components/layout/physio-layout";
import { OTLayout } from "@/components/layout/ot-layout";
import { AlertTriangle, CheckCircle2, ClipboardList, Loader2 } from "lucide-react";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";

type ConcernType = "clinical-content-error" | "ai-response-concern" | "data-issue" | "other";

const CONCERN_TYPES: { value: ConcernType; label: string; description: string }[] = [
  {
    value: "clinical-content-error",
    label: "Clinical content error",
    description: "Incorrect, outdated, or potentially harmful clinical information",
  },
  {
    value: "ai-response-concern",
    label: "AI response concern",
    description: "An AI response that was inappropriate, unsafe, or misleading",
  },
  {
    value: "data-issue",
    label: "Data issue",
    description: "Incorrect data displayed, missing information, or data integrity concern",
  },
  {
    value: "other",
    label: "Other",
    description: "Any other safety concern or platform bug with patient safety implications",
  },
];

type FormState = "idle" | "submitting" | "success" | "error";

interface SuccessData {
  referenceNumber: string;
}

type LayoutComponent = React.ComponentType<{ children: React.ReactNode; title: string }>;

function getRoleLayout(role: string | null): LayoutComponent {
  switch (role) {
    case "doctor": return DoctorLayout;
    case "pharmacist": return PharmacistLayout;
    case "physio": return PhysioLayout;
    case "ot": return OTLayout;
    default: return NurseLayout;
  }
}

export default function HcpSafetyReportPage() {
  const role = localStorage.getItem("rheumhub_role");
  const Layout = getRoleLayout(role);

  const [concernType, setConcernType] = useState<ConcernType | "">("");
  const [description, setDescription] = useState("");
  const [pageOrFeature, setPageOrFeature] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  const isValid = concernType !== "" && description.trim().length >= 10;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    setFormState("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/safety-reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          concernType,
          description: description.trim(),
          pageOrFeature: pageOrFeature.trim() || null,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `Unexpected error (${res.status})`);
      }

      const data = await res.json() as { referenceNumber: string };
      setSuccessData({ referenceNumber: data.referenceNumber });
      setFormState("success");
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setFormState("error");
    }
  }

  if (formState === "success" && successData) {
    return (
      <Layout title="Report a Safety Concern">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-teal-50 rounded-full">
                <CheckCircle2 className="h-10 w-10 text-teal-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Report submitted</h2>
            <p className="text-slate-600 text-sm mb-6 leading-relaxed">
              Thank you for reporting this concern. The Clinical Safety Officer has been notified and will investigate in line with DCB0129 procedures.
            </p>
            <div className="bg-slate-50 rounded-xl border border-slate-200 px-6 py-4 mb-6 inline-block w-full">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Your reference number</p>
              <p className="text-2xl font-bold text-slate-900 font-mono tracking-wide">{successData.referenceNumber}</p>
              <p className="text-xs text-slate-400 mt-1">Please keep this for your records</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 text-left mb-6">
              <strong>If this is an immediate patient safety risk,</strong> please also contact your clinical manager and report via your organisation's standard incident reporting procedures.
            </div>
            <div className="flex flex-col items-center gap-3">
              <Link
                href="/hcp/safety-reports"
                className="flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-900 bg-teal-50 hover:bg-teal-100 border border-teal-200 px-4 py-2.5 rounded-xl transition-colors"
              >
                <ClipboardList className="h-4 w-4" />
                View all my reports
              </Link>
              <button
                onClick={() => {
                  setConcernType("");
                  setDescription("");
                  setPageOrFeature("");
                  setSuccessData(null);
                  setFormState("idle");
                }}
                className="text-sm text-slate-500 hover:text-slate-700 font-medium underline"
              >
                Submit another report
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Report a Safety Concern">
      <div className="max-w-xl mx-auto space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <strong>DCB0129 incident reporting</strong> — Use this form to report clinical content errors, unsafe AI responses, data integrity issues, or other safety concerns. Your report will be stored securely and the Clinical Safety Officer will be notified immediately.
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-6">
          <div>
            <p className="block text-sm font-semibold text-slate-800 mb-3">
              Type of concern <span className="text-red-500">*</span>
            </p>
            <div className="space-y-2">
              {CONCERN_TYPES.map((ct) => (
                <label
                  key={ct.value}
                  aria-label={ct.label}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    concernType === ct.value
                      ? "border-amber-400 bg-amber-50"
                      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="concernType"
                    value={ct.value}
                    checked={concernType === ct.value}
                    onChange={() => setConcernType(ct.value)}
                    className="mt-0.5 accent-amber-500 shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{ct.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{ct.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="description" className="block text-sm font-semibold text-slate-800">
                Description <span className="text-red-500">*</span>
              </label>
              <VoiceInputButton
                tone="amber"
                size="xs"
                onTranscript={(chunk) => setDescription((prev) => appendVoiceChunk(prev, chunk))}
                label="Dictate description"
              />
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Describe the concern clearly. Include what you saw, why it is a concern, and any potential patient safety implications.
            </p>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="e.g. The dosing information for methotrexate on the Conditions page states 25mg weekly as a starting dose — the standard UK starting dose is 7.5–10mg weekly per NICE guidance..."
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">
              {description.trim().length} characters{description.trim().length < 10 && description.length > 0 ? " (minimum 10)" : ""}
            </p>
          </div>

          <div>
            <label htmlFor="pageOrFeature" className="block text-sm font-semibold text-slate-800 mb-1.5">
              Page or feature affected <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              id="pageOrFeature"
              type="text"
              value={pageOrFeature}
              onChange={(e) => setPageOrFeature(e.target.value)}
              placeholder="e.g. Conditions / Methotrexate, AI Assistant, Pharmacology page"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          {formState === "error" && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-slate-400">
              Reports are stored securely and sent to{" "}
              <span className="font-medium">safety@medantixhealth.co.uk</span>
            </p>
            <button
              type="submit"
              disabled={!isValid || formState === "submitting"}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-200 disabled:text-slate-400 text-white disabled:cursor-not-allowed font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors"
            >
              {formState === "submitting" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  Submit report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
